import { useState, useEffect } from 'react';
import { Tag } from '../components/Tag';
import { api, type ApiOrganisation } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isSafeUrl } from '../utils/url';
import styles from './OrganisationsScreen.module.css';

export function OrganisationsScreen() {
  const [organisations, setOrganisations] = useState<ApiOrganisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', website: '', contactEmail: '', ticketTailorAccountRef: '' });
  const { user } = useAuth();

  useEffect(() => {
    api.organisations.list()
      .then(setOrganisations)
      .catch(() => setOrganisations([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await api.organisations.create(
        formData.name,
        formData.description || null,
        formData.website || null,
        formData.contactEmail || null,
        formData.ticketTailorAccountRef || null
      );
      setOrganisations(prev => [...prev, {
        ...created,
        description: formData.description || null,
        website: formData.website || null,
        contact_email: formData.contactEmail || null,
        is_concierge: false,
        created_at: new Date().toISOString()
      }]);
      setFormData({ name: '', description: '', website: '', contactEmail: '', ticketTailorAccountRef: '' });
      setShowForm(false);
      setActionError(null);
    } catch (err) {
      setActionError('Failed to create organisation. Please try again.');
    }
  };

  const filtered = organisations;

  return (
    <div>
      <div className={styles.container}>
        <h2 className={styles.headerTitle}>Organisations</h2>
        <p className={styles.headerSubtitle}>
          Organisations that run events on the platform.
        </p>

        {user && user.role === 'admin' && (
          <button
            onClick={() => setShowForm(!showForm)}
            className={styles.adminButton}
          >
            {showForm ? 'Cancel' : 'Create Organisation'}
          </button>
        )}

        {showForm && (
          <form onSubmit={handleCreate} className={styles.formContainer}>
            <div className={styles.formField}>
              <label htmlFor="org-name" className={styles.formLabel}>Name *</label>
              <input
                id="org-name"
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="org-description" className={styles.formLabel}>Description</label>
              <textarea
                id="org-description"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className={styles.formTextarea}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="org-website" className={styles.formLabel}>Website</label>
              <input
                id="org-website"
                type="url"
                value={formData.website}
                onChange={e => setFormData({ ...formData, website: e.target.value })}
                className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="org-contact-email" className={styles.formLabel}>Contact email</label>
              <input
                id="org-contact-email"
                type="email"
                value={formData.contactEmail}
                onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <label htmlFor="org-tt-ref" className={styles.formLabel}>Ticket Tailor account ref</label>
              <input
                id="org-tt-ref"
                type="text"
                value={formData.ticketTailorAccountRef}
                onChange={e => setFormData({ ...formData, ticketTailorAccountRef: e.target.value })}
                className={styles.formInput}
              />
            </div>
            <button
              type="submit"
              className={styles.submitButton}
            >
              Create Organisation
            </button>
          </form>
        )}

        {actionError && (
          <div className={styles.errorBanner}>
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} className={styles.errorClose}>×</button>
          </div>
        )}

        {loading ? (
          <div className={styles.loading}>
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.noData}>
            No organisations found.
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>Website</th>
                <th className={styles.th}>Contact email</th>
                <th className={styles.th}>Concierge</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((org) => (
                <tr key={org.id}>
                  <td className={styles.td}>
                    <div className={styles.tdName}>{org.name}</div>
                    {org.description && (
                      <div className={styles.tdDescription}>
                        {org.description}
                      </div>
                    )}
                  </td>
                  <td className={styles.td}>
                    {org.website && isSafeUrl(org.website) ? (
                      <a href={org.website} target="_blank" rel="noopener noreferrer" className={styles.tdLink}>
                        {org.website}
                      </a>
                    ) : (
                      <span className={styles.tdPlaceholder}>—</span>
                    )}
                  </td>
                  <td className={styles.tdEmail}>
                    {org.contact_email ? (
                      <a href={`mailto:${org.contact_email}`} className={styles.tdEmailLink}>
                        {org.contact_email}
                      </a>
                    ) : (
                      <span className={styles.tdPlaceholder}>—</span>
                    )}
                  </td>
                  <td className={styles.td}>
                    <Tag variant={org.is_concierge ? 'accent' : 'neutral'}>
                      {org.is_concierge ? 'Yes' : 'No'}
                    </Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
